import React, { useRef, useState } from "react";
import { createPortal } from "react-dom";
import listStyles from "../FinanceListV2.module.scss";
import styles from "./TodosProgressionBadge.module.scss";

const GRAY = { bg: [238, 241, 244], text: [92, 107, 122], border: [215, 222, 228] };
const MAUVE = { bg: [247, 229, 243], text: [162, 62, 140], border: [227, 169, 220] };

const toHex = ([r, g, b]) =>
  "#" +
  [r, g, b]
    .map((c) => Math.round(c).toString(16).padStart(2, "0"))
    .join("");

const mix = (from, to, t) => from.map((v, i) => v + (to[i] - v) * t);

// Dégradé continu gris (0 coché) -> mauve vif (tout coché), plutôt qu'une
// palette à teintes discrètes (moins "guirlande de Noël"). Courbe en racine
// carrée (et non linéaire) : sur un ratio réaliste (ex. 1 ou 2 todos cochés
// sur 8), un dégradé linéaire reste visuellement quasi gris jusqu'à
// approcher 100 % — la racine carrée rend la teinte perceptible dès le
// premier todo coché, tout en gardant les deux bornes (0 => gris, 1 => mauve).
export const resolveTodosBadgeColors = (coches, total) => {
  const ratio = total > 0 ? Math.min(Math.max(coches / total, 0), 1) : 0;
  const t = Math.sqrt(ratio);

  return {
    background: toHex(mix(GRAY.bg, MAUVE.bg, t)),
    color: toHex(mix(GRAY.text, MAUVE.text, t)),
    borderColor: toHex(mix(GRAY.border, MAUVE.border, t)),
  };
};

// LS-647 — badge "X/Y" de la liste des demandes, avec infobulle (portal, comme
// FilterPopover) listant les todos non cochés au survol. Portal nécessaire
// car le badge est dans <table class="…" style="overflow:hidden"> : une
// bulle simplement position:absolute y serait rognée. Couleur en dégradé
// continu selon le taux de complétion.
const TodosProgressionBadge = ({ progression }) => {
  const [position, setPosition] = useState(null);
  const badgeRef = useRef(null);

  const show = () => {
    const rect = badgeRef.current.getBoundingClientRect();
    setPosition({ top: rect.top - 8, left: rect.left + rect.width / 2 });
  };

  const hide = () => setPosition(null);

  const { coches, total, todosAFaire } = progression;
  const { background, color, borderColor } = resolveTodosBadgeColors(coches, total);

  return (
    <>
      <span
        ref={badgeRef}
        className={listStyles.badge}
        style={{ background, color, border: `1px solid ${borderColor}` }}
        tabIndex={0}
        onMouseOver={show}
        onMouseOut={hide}
        onFocus={show}
        onBlur={hide}
      >
        {coches}/{total}
      </span>
      {position &&
        createPortal(
          <div
            className={styles.bubble}
            style={{ top: position.top, left: position.left }}
          >
            <div className={styles.arrow} />
            {todosAFaire.length > 0 ? (
              <>
                <div className={styles.title}>À faire</div>
                <ul className={styles.list}>
                  {todosAFaire.map((libelle) => (
                    <li key={libelle}>{libelle}</li>
                  ))}
                </ul>
              </>
            ) : (
              <div className={styles.title}>Tous les todos sont cochés</div>
            )}
          </div>,
          document.body,
        )}
    </>
  );
};

export default TodosProgressionBadge;
